import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Sparkles, Mail, Lock, User, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { API } from "../lib/api";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "@/components/LanguageSelector";
export default function LoginPage() {
  const { t } = useTranslation();
  const [isRegister, setIsRegister] = useState(false);
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    role: "",  // must be chosen by user during registration
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();

  // Auto-resume tras login. Aceptamos dos query params, en este orden:
  //   1. `?next=<path>` — lo inyecta el interceptor 401 de `lib/api.js`
  //      cuando una petición autenticada falla por sesión expirada.
  //      Caso típico: el admin estaba editando algo y se le caducó la
  //      sesión → al re-loguear vuelve exactamente a esa pantalla.
  //   2. `?returnTo=<path>` — flujo de pago (LoginRequiredModal) para que
  //      el fan vuelva a la pantalla de compra tras crearse una cuenta.
  //
  // Reglas anti open-redirect: la ruta debe empezar por `/`, NO por `//`,
  // y no contener `:` (esquema). Si falla la validación se ignora.
  const returnTo = (() => {
    try {
      const sp = new URLSearchParams(location.search);
      const candidate = sp.get("next") || sp.get("returnTo");
      if (
        candidate &&
        candidate.startsWith("/") &&
        !candidate.startsWith("//") &&
        !candidate.includes(":")
      ) {
        // Don't bounce back to a login/register loop.
        const path = candidate.split("?")[0];
        if (path !== "/login" && path !== "/register") return candidate;
      }
    } catch { /* noop */ }
    return null;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        if (!form.role) {
          toast.error(t("auth.errorRole"));
          setLoading(false);
          return;
        }
        const res = await axios.post(
          `${API}/auth/register`,
          {
            ...form,
            websites: [],
          },
          { withCredentials: true }
        );
        setUser(res.data);
        toast.success(t("auth.accountCreated"));
        navigate(returnTo || "/profile");
      } else {
        const res = await axios.post(
          `${API}/auth/login`,
          { email: form.email, password: form.password },
          { withCredentials: true }
        );
        setUser(res.data);
        toast.success(t("auth.successLogin"));
        if (returnTo) {
          navigate(returnTo);
        } else {
          navigate(res.data.role === "admin" ? "/admin" : (res.data.role === "consumer" ? "/" : "/dashboard"));
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  // Detecta iPhone/iPad para mostrar aviso del selector de cuentas Google
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  const handleGoogleLogin = () => {
    // Persist the auto-resume target so AuthCallback can pick it up after
    // the OAuth round-trip (the redirect URL itself can't change — Google
    // requires it to be pre-registered, see warning below).
    if (returnTo) {
      try { sessionStorage.setItem("startip_post_login_redirect", returnTo); } catch { /* noop */ }
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md space-y-6"
      >
        <div className="text-center relative">
          <Link to="/" className="inline-flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-neon-pink" />
            <h1 className="font-heading font-extrabold text-2xl text-white uppercase">
              STAR<span className="text-neon-pink">TIP</span>
            </h1>
          </Link>
          <p className="mt-2 text-zinc-400 font-body">
            {isRegister ? t("auth.register") : t("auth.login")}
          </p>
        </div>

        <Button
          onClick={handleGoogleLogin}
          className="w-full h-14 bg-white hover:bg-zinc-100 text-black font-body font-semibold rounded-xl"
          data-testid="google-login-btn"
        >
          <img
            src="https://www.google.com/favicon.ico"
            alt="G"
            className="w-5 h-5 mr-2"
          />
          {t("auth.googleLogin")}
        </Button>

        {isIOS && (
          <p className="text-[11px] text-zinc-500 font-body text-center -mt-2 px-2">
            En iPhone: si no aparece el selector de cuentas, escribe tu dirección Gmail manualmente en la pantalla de Google.
          </p>
        )}

        <div className="flex items-center gap-3">
          <div className="flex-1 h-px bg-border" />
          <span className="text-sm text-zinc-500 font-body">{t("auth.orContinueWith")}</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {isRegister && (
            <>
              {/* Role selector — shown first so users consciously choose */}
              <div className="space-y-2">
                <p className="text-xs text-zinc-400 font-body font-semibold uppercase tracking-wide">
                  {t("auth.chooseRole")}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, role: "consumer" })}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      form.role === "consumer"
                        ? "border-neon-pink bg-neon-pink/10 text-white"
                        : "border-border bg-card text-zinc-400 hover:border-neon-pink/50"
                    }`}
                    data-testid="register-role-fan"
                  >
                    <p className="font-heading font-bold text-sm">{t("auth.fan")}</p>
                    <p className="text-[10px] mt-0.5 opacity-70">{t("auth.fanDescriptionShort")}</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, role: "creator" })}
                    className={`p-3 rounded-xl border-2 text-left transition-all ${
                      form.role === "creator"
                        ? "border-neon-green bg-neon-green/10 text-white"
                        : "border-border bg-card text-zinc-400 hover:border-neon-green/50"
                    }`}
                    data-testid="register-role-creator"
                  >
                    <p className="font-heading font-bold text-sm">{t("auth.creator")}</p>
                    <p className="text-[10px] mt-0.5 opacity-70">{t("auth.creatorDescriptionShort")}</p>
                  </button>
                </div>
              </div>

              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  placeholder={t("auth.name")}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="pl-10 bg-transparent border-2 border-border rounded-xl h-12 text-white"
                  required
                  data-testid="register-name"
                />
              </div>
            </>
          )}

          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              type="email"
              placeholder={t("auth.email")}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="pl-10 bg-transparent border-2 border-border rounded-xl h-12 text-white"
              required
              data-testid="login-email"
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              type={showPassword ? "text" : "password"}
              placeholder={t("auth.password")}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="pl-10 pr-10 bg-transparent border-2 border-border rounded-xl h-12 text-white"
              required
              data-testid="login-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
              data-testid="toggle-password-visibility"
              aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-14 bg-neon-pink hover:bg-neon-pink/90 text-white font-heading font-bold uppercase rounded-xl shadow-[4px_4px_0px_0px_rgba(255,0,127,0.25)]"
            data-testid="login-submit-btn"
          >
            {loading
              ? "..."
              : isRegister
                ? t("auth.submitRegister")
                : t("auth.submitLogin")}
          </Button>
        </form>

        <p className="text-center text-sm text-zinc-400 font-body">
          {isRegister ? t("auth.alreadyHaveAccount") : t("auth.noAccount")}
          <button
            onClick={() => isRegister ? setIsRegister(false) : navigate("/register")}
            className="ml-1 text-neon-pink hover:underline"
            data-testid="toggle-auth-mode"
          >
            {isRegister ? t("auth.login") : t("auth.register")}
          </button>
        </p>
      </motion.div>
    </div>
  );
}
